import React, { useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { createBooking, fetchQuote } from "@/api";
import { toast } from "sonner";

export default function BookingSection({ services, booked, selectedServiceId, onSelectService }) {
  const { lang, t } = useI18n();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [consent, setConsent] = useState(false);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    service_id: selectedServiceId || "",
    booking_date: null,
    pickup_time: "10:00",
    pickup_location: "",
    dropoff_location: "",
    passengers: 2,
    luggage: 2,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    flight_number: "",
    notes: "",
  });

  // Sync selectedServiceId from parent
  React.useEffect(() => {
    if (selectedServiceId && selectedServiceId !== form.service_id) {
      setForm((f) => ({ ...f, service_id: selectedServiceId }));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId]);

  const service = useMemo(
    () => services.find((s) => s.id === form.service_id),
    [services, form.service_id],
  );

  // Live quote calculation
  React.useEffect(() => {
    if (form.service_id && form.booking_date && form.pickup_time) {
      const dateStr = format(form.booking_date, "yyyy-MM-dd");
      fetchQuote(form.service_id, dateStr, form.pickup_time)
        .then(setQuote)
        .catch(() => setQuote(null));
    } else {
      setQuote(null);
    }
  }, [form.service_id, form.booking_date, form.pickup_time]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goNext = () => {
    if (step === 1) {
      if (!form.service_id) {
        toast.error(t.booking.service_label);
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!form.booking_date) {
        toast.error(t.booking.pick_date_first);
        return;
      }
      if (!form.pickup_location || !form.dropoff_location) {
        toast.error(t.booking.required);
        return;
      }
      setStep(3);
    }
  };

  const submitAndPay = async () => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error(t.booking.required);
      return;
    }
    if (!consent) {
      toast.error(lang === "it" ? "Devi accettare Termini e Privacy" : "You must accept Terms and Privacy");
      return;
    }
    try {
      setSubmitting(true);
      const dateStr = format(form.booking_date, "yyyy-MM-dd");
      const payload = {
        service_id: form.service_id,
        booking_date: dateStr,
        pickup_time: form.pickup_time,
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        passengers: Number(form.passengers),
        luggage: Number(form.luggage),
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        flight_number: form.flight_number || null,
        notes: form.notes || null,
        language: lang,
      };
      const booking = await createBooking(payload);
      setConfirmedBooking(booking);
      toast.success(t.booking.success_title);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const locale = lang === "it" ? itLocale : enUS;
  const dateFns = (d) => format(d, "PPP", { locale });

  // Booked dates count: highlight (not block) days with bookings
  const bookedDays = useMemo(() => {
    const map = new Map();
    Object.entries(booked || {}).forEach(([d, slots]) => {
      map.set(d, slots.length);
    });
    return map;
  }, [booked]);

  const bookedModifier = (day) => {
    const key = format(day, "yyyy-MM-dd");
    return bookedDays.has(key);
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] accent-text">{t.booking.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl brand-text mt-4 leading-tight">
            {t.booking.title}
          </h2>
          <div data-testid="trust-badges" className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: "💶", label: t.booking.trust_payment },
              { icon: "🔒", label: t.booking.trust_ssl },
              { icon: "🛡️", label: t.booking.trust_gdpr },
              { icon: "✅", label: t.booking.trust_no_card },
            ].map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-1.5 text-xs font-medium brand-text bg-[#F9F7F3] border border-slate-200 rounded-full px-3 py-1.5"
              >
                <span aria-hidden="true">{b.icon}</span>
                {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="bg-[#F9F7F3] rounded-2xl p-6 md:p-10 border border-slate-200">
            {confirmedBooking ? (
              <div data-testid="booking-confirmed" className="py-8 text-center">
                <CheckCircle2 className="w-16 h-16 mx-auto accent-text" />
                <h3 className="font-display text-3xl brand-text mt-5">{t.booking.success_title}</h3>
                <p className="text-slate-600 mt-3 max-w-md mx-auto">{t.booking.success_msg}</p>
                <div className="mt-6 inline-block bg-white border border-slate-200 rounded-xl px-5 py-3 text-sm text-slate-600">
                  <span className="uppercase tracking-widest text-xs text-slate-400 mr-2">ID</span>
                  <span className="font-mono brand-text">{confirmedBooking.id.slice(0, 8)}</span>
                </div>
                <div className="mt-8">
                  <Button
                    data-testid="booking-new-btn"
                    onClick={() => {
                      setConfirmedBooking(null);
                      setStep(1);
                      setForm({
                        service_id: "",
                        booking_date: null,
                        pickup_time: "10:00",
                        pickup_location: "",
                        dropoff_location: "",
                        passengers: 2,
                        luggage: 2,
                        customer_name: "",
                        customer_email: "",
                        customer_phone: "",
                        flight_number: "",
                        notes: "",
                      });
                    }}
                    variant="outline"
                    className="rounded-full"
                  >
                    {t.nav.book_now}
                  </Button>
                </div>
              </div>
            ) : (
            <>
            {/* Steps */}
            <div className="flex items-center gap-2 mb-8">
              {[t.booking.step1, t.booking.step2, t.booking.step3].map((label, i) => {
                const idx = i + 1;
                const active = idx === step;
                const done = idx < step;
                return (
                  <div key={label} className="flex items-center gap-2 flex-1">
                    <div
                      className={`w-8 h-8 rounded-full grid place-items-center text-sm ${
                        active
                          ? "brand-bg text-white"
                          : done
                          ? "accent-bg text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {idx}
                    </div>
                    <span
                      className={`text-sm ${active ? "brand-text font-medium" : "text-slate-500"}`}
                    >
                      {label}
                    </span>
                    {i < 2 && <div className="flex-1 h-px bg-slate-200" />}
                  </div>
                );
              })}
            </div>

            {step === 1 && (
              <div className="space-y-4">
                <Label>{t.booking.service_label}</Label>
                <Select
                  value={form.service_id}
                  onValueChange={(v) => {
                    setField("service_id", v);
                    onSelectService && onSelectService(v);
                  }}
                >
                  <SelectTrigger data-testid="booking-service-select" className="bg-white">
                    <SelectValue placeholder={t.booking.service_label} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`booking-service-opt-${s.id}`}>
                        {lang === "it" ? s.name_it : s.name_en}
                        {s.category === "custom" || s.price_eur === 0
                          ? ` — ${lang === "it" ? "preventivo su richiesta" : "custom quote"}`
                          : ` — €${s.price_eur.toFixed(0)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="pt-4 flex justify-end">
                  <Button
                    data-testid="booking-step1-next"
                    onClick={goNext}
                    className="brand-bg text-white rounded-full px-6"
                  >
                    {t.booking.next}
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.booking.date_label}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="booking-date-trigger"
                        variant="outline"
                        className="w-full justify-start bg-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.booking_date ? dateFns(form.booking_date) : t.booking.date_label}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.booking_date}
                        onSelect={(d) => setField("booking_date", d)}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        modifiers={{ booked: bookedModifier }}
                        modifiersStyles={{
                          booked: {
                            background: "#C5A05933",
                            color: "#0A2463",
                            fontWeight: 600,
                          },
                        }}
                        locale={locale}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>{t.booking.time_label}</Label>
                  <Input
                    type="time"
                    data-testid="booking-time-input"
                    value={form.pickup_time}
                    onChange={(e) => setField("pickup_time", e.target.value)}
                    className="bg-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>{t.booking.pickup}</Label>
                  <Input
                    data-testid="booking-pickup-input"
                    value={form.pickup_location}
                    onChange={(e) => setField("pickup_location", e.target.value)}
                    placeholder="Olbia Airport"
                    className="bg-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>{t.booking.dropoff}</Label>
                  <Input
                    data-testid="booking-dropoff-input"
                    value={form.dropoff_location}
                    onChange={(e) => setField("dropoff_location", e.target.value)}
                    placeholder="Porto Cervo, Hotel Cala di Volpe"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>{t.booking.passengers}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={4}
                    data-testid="booking-passengers-input"
                    value={form.passengers}
                    onChange={(e) => setField("passengers", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>{t.booking.luggage}</Label>
                  <Input
                    type="number"
                    min={0}
                    max={8}
                    data-testid="booking-luggage-input"
                    value={form.luggage}
                    onChange={(e) => setField("luggage", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="md:col-span-2 pt-2 flex justify-between">
                  <Button
                    data-testid="booking-step2-back"
                    variant="ghost"
                    onClick={() => setStep(1)}
                  >
                    {t.booking.back}
                  </Button>
                  <Button
                    data-testid="booking-step2-next"
                    onClick={goNext}
                    className="brand-bg text-white rounded-full px-6"
                  >
                    {t.booking.next}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.booking.name}</Label>
                  <Input
                    data-testid="booking-name-input"
                    value={form.customer_name}
                    onChange={(e) => setField("customer_name", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>{t.booking.email}</Label>
                  <Input
                    type="email"
                    data-testid="booking-email-input"
                    value={form.customer_email}
                    onChange={(e) => setField("customer_email", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>{t.booking.phone}</Label>
                  <Input
                    data-testid="booking-phone-input"
                    value={form.customer_phone}
                    onChange={(e) => setField("customer_phone", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>{t.booking.flight}</Label>
                  <Input
                    data-testid="booking-flight-input"
                    value={form.flight_number}
                    onChange={(e) => setField("flight_number", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>{t.booking.notes}</Label>
                  <Textarea
                    data-testid="booking-notes-input"
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="md:col-span-2 pt-2 flex flex-col gap-3">
                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="booking-consent-checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      {lang === "it"
                        ? "Ho letto e accetto i "
                        : "I have read and accept the "}
                      <a href="/terms" target="_blank" rel="noreferrer" className="underline brand-text">
                        {lang === "it" ? "Termini" : "Terms"}
                      </a>
                      {" "}{lang === "it" ? "e l'" : "and "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="underline brand-text">
                        {lang === "it" ? "Informativa Privacy" : "Privacy Policy"}
                      </a>.
                    </span>
                  </label>
                  <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-4 py-3">
                    💳 {t.booking.payment_note}
                  </div>
                  <div className="flex justify-between">
                    <Button
                      data-testid="booking-step3-back"
                      variant="ghost"
                      onClick={() => setStep(2)}
                    >
                      {t.booking.back}
                    </Button>
                    <Button
                      data-testid="booking-pay-btn"
                      onClick={submitAndPay}
                      disabled={submitting}
                      className="brand-bg text-white rounded-full px-7"
                    >
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {t.booking.pay}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </div>

          {/* Summary */}
          <aside
            data-testid="booking-summary"
            className="bg-[#0A2463] text-white rounded-2xl p-7 h-fit sticky top-28"
          >
            <p className="text-xs uppercase tracking-[0.3em] accent-text">
              Chrysler 300C Touring
            </p>
            <h3 className="font-display text-2xl mt-2">
              {service
                ? lang === "it"
                  ? service.name_it
                  : service.name_en
                : t.booking.service_label}
            </h3>
            <div className="mt-6 space-y-3 text-sm text-white/80">
              <div className="flex justify-between">
                <span>{t.booking.date_label}</span>
                <span>{form.booking_date ? dateFns(form.booking_date) : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.booking.time_label}</span>
                <span>{form.pickup_time || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.booking.passengers}</span>
                <span>{form.passengers}</span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              {quote && quote.breakdown && quote.breakdown.length > 1 && (
                <div className="space-y-1 mb-3 text-xs text-white/70">
                  {quote.breakdown.map((b, i) => (
                    <div key={b.label} className="flex justify-between">
                      <span>{b.label}</span>
                      <span>{i === 0 ? "" : "+"}€{b.amount.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-sm uppercase tracking-widest text-white/70">
                  {t.booking.total}
                </span>
                <span className="font-display text-3xl accent-text" data-testid="booking-total">
                  {service && (service.category === "custom" || service.price_eur === 0)
                    ? (lang === "it" ? "Su richiesta" : "On request")
                    : `€${quote ? quote.final.toFixed(0) : service ? service.price_eur.toFixed(0) : "—"}`}
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
